import { useState, useEffect, useCallback } from 'react';
import { computeMatches, ApiError } from '../lib/api';
import type { MatchResult } from '../types';

interface UseMatchResultsReturn {
  data: MatchResult | null;
  isLoading: boolean;
  error: string | null;
  errorStatus: number | null;
  refetch: () => void;
}

export default function useMatchResults(
  sessionId: string,
  partnerToken: string | null,
): UseMatchResultsReturn {
  const [data, setData] = useState<MatchResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);

  const fetch = useCallback(async () => {
    if (!sessionId || !partnerToken) {
      setIsLoading(false);
      setError('No partner token found. Open this page in the same browser where you took the quiz.');
      setErrorStatus(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    setErrorStatus(null);

    try {
      const result = await computeMatches(sessionId, partnerToken);
      setData(result);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setErrorStatus(err.status);
      } else {
        setError('An unexpected error occurred.');
        setErrorStatus(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, partnerToken]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, isLoading, error, errorStatus, refetch: fetch };
}
